import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    OnDestroy,
    OnInit,
    signal, viewChild
} from '@angular/core';
import {StateService} from "../../services/state.service";
import {DomainInfo, DomainService, EppFullDomainInfo, NameServerGroup} from "../../services/domain.service";
import { HttpErrorResponse } from "@angular/common/http";
import {AlertService} from "../../services/alert.service";
import {filter, finalize, map, mergeMap} from "rxjs/operators";
import {formatNumber, NgClass} from "@angular/common";
import {BusyService} from "../../services/busy.service";
import {Transaction, TransactionService} from "../../services/transaction.service";
import {
    TransactionDetailsDialogComponent
} from "../../dialogs/transaction-details-dialog/transaction-details-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {UtilService} from "../../services/util.service";
import {AuthService, User} from "../../services/auth.service";
import {FormsModule} from "@angular/forms";
import {Constants} from "../../util/constants";
import {merge, Subscription, timer} from "rxjs";
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    MatTableDataSource
} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {NotificationService} from "../../services/notification.service";
import {OkInfo} from "../../util/interfaces";
import {ActivatedRoute, Router} from "@angular/router";
import {MatAutocomplete, MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {CacheService} from "../../services/cache.service";
import {ALL_DOMAINS_LABEL, CacheMessageMapper, TagInfo, throttleReloads} from "../../util/cache-message-mapper";
import {MatLabel, MatSelect} from '@angular/material/select';
import {MatTooltip} from '@angular/material/tooltip';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatOption} from '@angular/material/core';
import {MatIcon} from '@angular/material/icon';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatInput} from '@angular/material/input';
import {MatError, MatFormField, MatSuffix} from '@angular/material/form-field';
import {DateTime} from "luxon";
import {form, FormField, pattern, required} from "@angular/forms/signals";
import {Utils} from "../../util/utils";
import {FieldErrorsComponent} from "../../controls/field-errors/field-errors.component";

const MIN_WIDTH: number = 320;
const MAX_WIDTH: number = 800;
const MIN_SEARCH_LENGTH = 3;
const BLACKOUT_PERIOD = 2000;

interface DomainModel {
    domain: string;
}

/** A push notification that concerns the displayed domain. */
interface DomainNotification {
    /** Set if it's a change in transactions, whose cached info must be cleared. */
    transInfo?: TagInfo;
    /** It's about a change spanning many domains, which may or may not include this one. */
    wildcard: boolean;
}

@Component({
    selector: 'app-domain',
    templateUrl: './domain.component.html',
    styleUrls: ['./domain.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatInput, MatAutocompleteTrigger, MatProgressSpinner, MatIconButton, MatSuffix, MatIcon,
        MatAutocomplete, MatOption, MatError, MatButton, MatCheckbox, MatTooltip, FormsModule, NgClass, MatSelect,
        MatPaginator, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef,
        MatHeaderRow, MatRowDef, MatRow, MatLabel, FieldErrorsComponent, FormField]
})
export class DomainComponent implements OnInit, OnDestroy {

    autoComplete = viewChild<MatAutocomplete>('auto');
    paginator = viewChild<MatPaginator>('paginator');

    domainModel = signal<DomainModel>({
        domain: ''
    });

    domainForm = form(this.domainModel, (schemaPath) => {
        required(schemaPath.domain, {message: Constants.ERR_REQUIRED});
        pattern(schemaPath.domain, Constants.DOMAIN_REGEX, {message: "Not a valid domain name"});
    });


    private prefix: string = "";
    private allMatches: string[] = [];
    private filterPrefix: string = "";
    private users: User[] = [];
    searching = signal(false);

    domainInfo = signal<DomainInfo|undefined>(undefined);
    eppDomainInfo = signal<EppFullDomainInfo|undefined>(undefined);
    allowToggleInRegistry = signal(false);
    // 2FA code to authorize the enabling of the Delete button
    allowDeleteCode = signal('');
    allowDelete = signal(false);
    groups = signal<NameServerGroup[]>([]);
    selectedGroup = signal('');
    dataSource = signal<MatTableDataSource<Transaction>|undefined>(undefined);
    private notifSub?: Subscription;
    private ignoreNotificationsUntilTime: number = 0;
    private readonly userRole: string;
    readonly isAdmin: boolean;


    readonly columnsToDisplay = ['id', 'user', 'op', 'started', 'notes', 'actions'];
    readonly DEFAULT_PAGE_SIZE = 10;

    eppBusy = signal(false);
    isBusy = computed(() => this.busyService.isBusy());

    transactions = signal<Transaction[]>([]);
    hasTransactions = computed(() => this.transactions().length > 0);
    hasPages = computed(() => this.transactions().length > this.DEFAULT_PAGE_SIZE);

    haveForm = signal(true);
    filteredMatches = signal<string[]>([]);

    flags = computed(() => this.domainInfo()? Object.keys(this.domainInfo()!.flags).sort() : []);
    domainExpired = computed(() => this.domainInfo()? this.domainInfo()!.expires < Date.now() : false);


    constructor(private state: StateService,
                private domainService: DomainService,
                private authService: AuthService,
                private transactionService: TransactionService,
                private alertService: AlertService,
                private busyService: BusyService,
                private utilService: UtilService,
                private notificationService: NotificationService,
                private route: ActivatedRoute,
                private router: Router,
                private cache: CacheService,
                private dialog: MatDialog) {

        this.userRole = this.state.get('userRole')();
        this.isAdmin = ["Admin User", "Super Admin"].includes(this.userRole);
        let domainName = this.route.snapshot.params['domain'];
        if (domainName) {
            this.state.set('domainName', domainName);
            this.router.navigateByUrl('/domains').then();
        } else {
            domainName = this.state.get('domainName')();
            if (domainName) {
                this.state.clear('domainName');
                this.initialize(domainName);
            }
        }

        // Hook up domain control to manage dynamic searches as user types
        effect(() => { this.handleDomainChange(); })
    }

    ngOnInit() {
        // Connect to push notification service if not yet connected (e.g. if page reloaded)
        this.notificationService.connect();

        // Initialize name server groups info in state, if not there yet
        this.busyService.showBusy();
        this.utilService.getGroups().pipe(
            finalize(() => { this.busyService.showNotBusy() })
        ).subscribe( {
            next: (groups) => {
                this.groups.set(groups);
            },
            error: (err: HttpErrorResponse) => {
                console.error(err);
                this.alertService.error("Error retrieving name server groups: " + err.message);
            }
        });
        // Register to receive push notifications about the displayed domain. A message naming it reloads at
        // once. A wildcard, sent for a change spanning many domains, may be about it too; a long bulk or
        // syncdates run can send one every second, so those reloads are throttled.
        const relevant$ = this.notificationService.message$.pipe(
            map(message => this.domainNotification(message)),
            filter((n): n is DomainNotification => n !== undefined)
        );
        this.notifSub = merge(
            relevant$.pipe(filter(n => !n.wildcard)),
            relevant$.pipe(filter(n => n.wildcard), throttleReloads())
        ).subscribe((n) => {
            // A change in transactions: clear any cached transactions info
            if (n.transInfo)
                this.cache.clear(...n.transInfo.tags);
            // Update its info from server, unless we are in a blackout period, to ignore notifications
            // triggered by ourselves
            const now = new Date().getTime();
            if (now > this.ignoreNotificationsUntilTime)
                this.update();
            else
                console.log("Ignoring notification due to blackout period");
        });
    }

    ngOnDestroy() {
        this.notifSub?.unsubscribe();
    }

    /** What a push notification means for the displayed domain, or undefined if it's not about it: no
     * domain is displayed, or it's not a transaction or domain change, or it names another domain.
     */
    private domainNotification(message: string): DomainNotification | undefined {
        if (!this.domainInfo())
            return undefined;
        const name: string = this.domainInfo()!.name;
        const transInfo = CacheMessageMapper.map(message, CacheMessageMapper.TRANSACTIONS_PAT);
        const info = transInfo ?? CacheMessageMapper.map(message, CacheMessageMapper.DOMAINS_PAT);
        if (!info)
            return undefined;
        const wildcard = info.label == ALL_DOMAINS_LABEL;
        if (!wildcard && info.label != name)
            return undefined;
        return {transInfo, wildcard};
    }

    /** Reacts to changes in the domain control's value signal to update matches being displayed
     */
    private handleDomainChange() {
        const domainControl = this.domainForm.domain();
        const value: string = domainControl.value().toLowerCase();
        if (!value)
            return;
        if (!this.prefix && value.length >= MIN_SEARCH_LENGTH && domainControl.invalid())
            this.updateMatches(value);
            // If input value is just a longer string with the original prefix: just filter the options from stored
        // array dynamically
        else if (this.prefix && value.length >= this.prefix.length && value.startsWith(this.prefix)) {
            this.filterPrefix = value;
            this.updateFilteredMatches();
            // If input value is no longer an extension of prefix: if shorter than min length, just clear all matches
            // otherwise set it as the new prefix and update matches
        } else if (!value.startsWith(this.prefix)) {
            if (value.length < MIN_SEARCH_LENGTH) {
                this.allMatches = [];
                this.prefix = this.filterPrefix = "";
                this.updateFilteredMatches()
            } else if (domainControl.invalid())
                this.updateMatches(value);
        }
    }

    private updateMatches(value: string) {
        this.prefix = value;
        this.filterPrefix = "";
        this.searching.set(true);
        this.domainService.find(value).pipe(
            finalize(() => {this.searching.set(false)})
        ).subscribe({
                next: (matches) => {
                    if (this.searching()) {
                        this.allMatches = matches;
                        this.updateFilteredMatches();
                    }
                },
                error: (err: HttpErrorResponse) => {
                    console.error(err);
                    this.alertService.error("Error finding domain matches: " + err.message);
                }
            }
        )
    }

    private updateFilteredMatches() {
        this.filteredMatches.set(this.allMatches.filter((m) => m.includes(this.filterPrefix)));
    }

    clearSearch() {
        this.domainForm().reset({domain: ''});
        this.allMatches = [];
        this.filteredMatches.set([]);
        this.searching.set(false);
    }

    clearSearchIfClosed() {
        if (!this.autoComplete()!.isOpen)
            this.clearSearch();
    }

    submitIfClosed() {
        if (!this.autoComplete()!.isOpen && !this.domainForm().invalid() && !this.isBusy())
            this.submit();
    }

    submit(event?: SubmitEvent) {
        event?.preventDefault();
        this.initialize(this.domainForm.domain().value().toLowerCase());
    }

    private initialize(domainName: string) {
        this.busyService.showBusy();
        this.domainInfo.set(undefined);
        this.transactions.set([]);
        this.domainService.getInfo(domainName).pipe(
            finalize(() => {
                this.busyService.showNotBusy();
            })
        ).subscribe({
            next: (out) => {
                this.domainInfo.set(out);
                this.clearSearch();
                this.filterPrefix = '';
                this.selectedGroup.set(this.domainInfo()!.ns_group.name);
                // Recreate the form so it will show as valid before typing new domain or prefix
                this.haveForm.set(false);
                timer(0).subscribe(() => {
                    this.haveForm.set(true);
                    this.updateEppInfo();
                    this.getTransactionInfo();
                });
            },
            error: (err: HttpErrorResponse) => {
                if (err.status == 404)
                    this.alertService.confirm("Domain not found in our db.<br/>Do you want to check if it is available?")
                        .subscribe((yesPlease) => {
                            if (yesPlease)
                                this.router.navigateByUrl(`/domains/buy/${domainName}`).then();
                        });
                else {
                    console.error(err);
                    this.alertService.error("Error getting domain info: " + err.message);
                }
            }
        });
    }

    update() {
        if (this.domainInfo()) {
            this.busyService.showBusy();
            this.allowToggleInRegistry.set(false);
            this.domainService.getInfo(this.domainInfo()!.name).pipe(
                finalize(() => {this.busyService.showNotBusy()})
            ).subscribe({
                next: (out) => {
                    this.domainInfo.set(out);
                    this.selectedGroup.set(this.domainInfo()!.ns_group.name);
                    this.updateEppInfo();
                    this.getTransactionInfo();
                },
                error: (err: HttpErrorResponse) => {
                    this.alertService.error("Error updating domain info: " + err.error || err.message);
                }
            });
        }
    }

    getInRegistryButtonLabel = computed(() =>
        this.domainInfo()?.in_registry? "Mark Not in Registry" : "Mark In Registry"
    )

    toggleInRegistry() {
        if (this.domainInfo()) {
            this.alertService.confirm("Are you sure you want to update this domain<br/>in our database to change its in-registry status?")
                .subscribe((sure) => {
                    if (sure) {
                        const newFlag: boolean = !this.domainInfo()!.in_registry;
                        this.busyService.showBusy();
                        this.ignoreNotificationsUntilTime = new Date().getTime() + BLACKOUT_PERIOD;
                        this.domainService.markInRegistry(this.domainInfo()!.name, newFlag).pipe(
                            finalize(() => {this.busyService.showNotBusy()})
                        ).subscribe({
                            next: (out) => {
                                this.domainInfo()!.in_registry = newFlag;
                                this.update();
                            },
                            error: this.utilService.getErrorHandler(`Error updating in-registry flag for ${this.domainInfo()?.name}`)
                        });
                    }
                });
        }
    }

    getInInquiryButtonLabel = computed(() =>
        this.domainInfo()?.flags['inquiry']? "Mark Not in Inquiry" : "Mark In Inquiry"
    )

    toggleFlag(flagName: string) {
        if (this.domainInfo()) {
            const newFlagValue: boolean = !this.domainInfo()!.flags[flagName];
            this.busyService.showBusy();
            this.ignoreNotificationsUntilTime = new Date().getTime() + BLACKOUT_PERIOD;
            this.domainService.updateDomainFlag(this.domainInfo()!.name, flagName, newFlagValue).pipe(
                finalize(() => {this.busyService.showNotBusy()})
            ).subscribe({
                next: (out) => {
                    this.domainInfo()!.flags[flagName] = newFlagValue;
                    this.update();
                },
                error: (err) => {
                    console.error(`Error updating ${flagName} flag for ${this.domainInfo()?.name}`, err);
                }
            });
        }
    }

    toggleInInquiry() {
        this.toggleFlag("inquiry");
    }

    canChangeGroup = computed(() =>
        !!this.selectedGroup() && !!this.domainInfo()?.in_registry && !this.hasServerStatus() && this.isAdmin
    );

    hasDifferentGroup = computed(() =>
        this.domainInfo()?.ns_group.name != this.selectedGroup()
    );

    changeGroup() {
        if (!this.domainInfo())
            return;
        this.busyService.showBusy();
        this.domainService.changeNameServerGroup(this.domainInfo()!.name, this.selectedGroup()).pipe(
            finalize(() => { this.busyService.showNotBusy() })
        ).subscribe({
            next: (out) => {
                // this.domainInfo()!.ns_group = this.groups().find((g) => g.name == this.selectedGroup()) as NameServerGroup;
                // this.updateEppInfo();
                this.update();
            },
            error: (err: HttpErrorResponse) => {
                this.alertService.error("Error changing domain's NS group: " + err.error || err.message);
            }
        });
    }

    private updateEppInfo() {
        this.eppDomainInfo.set(undefined);
        if (!this.domainInfo() || !this.domainInfo()!.in_registry)
          return;
        this.eppBusy.set(true);
        this.eppDomainInfo.set(undefined);
        this.domainService.eppGetInfo(this.domainInfo()!.name).pipe(
            finalize(() => {this.eppBusy.set(false)})
        ).subscribe({
            next: (out) => {
                console.log("EPP info:", out);
                if (out.in_registry) {
                    this.eppDomainInfo.set(out as EppFullDomainInfo);
                    //this.validateAgainstEpp();
                }
            },
            error: this.utilService.getErrorHandler("Error updating EPP domain info")
        });
    }

    private getTransactionInfo() {
        this.busyService.showBusy();
        this.utilService.getUsers().pipe(
            mergeMap((users) => {
                this.users = users;
                return this.transactionService.domainTransactions(this.domainInfo()!.name);
            }),
            finalize(() => { this.busyService.showNotBusy() })
        ).subscribe({
            next: (transactions) => {
                this.transactions.set(transactions || []);
                this.dataSource.set(new MatTableDataSource<Transaction>(transactions));
                this.dataSource()!.paginator = this.paginator()!;
            },
            error: (err: HttpErrorResponse) => {
                this.alertService.error("Error retrieving domain transaction info: " + err.error || err.message);
            }
        });
    }

    userEmail(userId: number): string {
        for (let user of this.users)
            if (user['id'] == userId)
                return Utils.MakeEmailBreakable(user['email']);
        return '?'
    }

    showDetail(trans: Transaction) {
        this.dialog.open(TransactionDetailsDialogComponent, {
            minWidth: MIN_WIDTH,
            maxWidth: MAX_WIDTH,
            data: {
                transactionId: trans.transId,
                actions: trans.actions
            }
        });
    }

    formatTimestamp(ts: number | undefined | null) {
        if (!ts)
            return '';
        // Hack to include any milliseconds after seconds (assuming ts ends like this: HH:MM:ss AM|PM)
        // Find :\d\d and insert milliseconds difference.
        const msec = formatNumber(ts % 1000, "en-US", "3.0-0");
        const sTimestamp = new Date(ts).toLocaleString();
        return sTimestamp.replace(/(:\d\d) /, `$1.${msec} `);
    }

    private validateAgainstEpp() {
        if (!this.eppDomainInfo() || !this.domainInfo())
            return;
        let errors = [];
        // Check if in_registry flag matches
        if (this.domainInfo()!.in_registry != this.eppDomainInfo()!.in_registry)
            errors.push("in_registry flag mismatch - EPP has: " + this.eppDomainInfo()!.in_registry)
        // Check if NS hosts match
        const sameHosts = this.domainInfo()!.ns_group.hosts.every((h) => this.eppDomainInfo()!.ns_hosts.includes(h));
        if (!sameHosts)
            errors.push("NS hosts mismatch - EPP has: " + this.eppDomainInfo()!.ns_hosts.join(", "));
        // Check if timestamps match
        if (this.domainInfo()!.registered != this.eppDomainInfo()!.created)
            errors.push("Registration time mismatch - EPP has: " + this.formatTimestamp(this.eppDomainInfo()!.created));
        if (this.domainInfo()!.expires != this.eppDomainInfo()!.expires)
            errors.push("Expiration time mismatch - EPP has: " + this.formatTimestamp(this.eppDomainInfo()!.expires));
        if (this.domainInfo()!.updated != this.eppDomainInfo()!.updated)
            errors.push("Last-updated time mismatch - EPP has: " + this.formatTimestamp(this.eppDomainInfo()!.updated));
        if (errors)
            this.alertService.warn(errors.join("<br/>"));
    }

    inRegistryMismatch = computed(() =>
        !!this.eppDomainInfo() && this.domainInfo()?.in_registry != this.eppDomainInfo()!.in_registry
    );

    nsMismatch = computed(() => {
        // Check if NS hosts match
        return !!this.eppDomainInfo() &&
            !this.domainInfo()!.ns_group.hosts.every((h) => this.eppDomainInfo()!.ns_hosts.includes(h));
    });

    registeredMismatch = computed(() =>
        !!this.eppDomainInfo() &&  this.domainInfo()?.registered != this.eppDomainInfo()!.created
    );

    expiresMismatch = computed(() =>
        !!this.eppDomainInfo() && this.domainInfo()?.expires != this.eppDomainInfo()!.expires
    );

    updatedMismatch = computed(() =>
        !!this.eppDomainInfo() && this.domainInfo()?.updated != this.eppDomainInfo()!.updated
    );

    anyDateMismatch = computed(() =>
        this.registeredMismatch() || this.expiresMismatch() || this.updatedMismatch()
    );

    statusMismatch = computed(() =>
        !!this.domainInfo() && !!this.eppDomainInfo() &&
        this.domainInfo()!.status.sort().join(',') != this.eppDomainInfo()!.statuses.sort().join(',')
    );

    getEppStatus = computed(() =>
        this.eppDomainInfo()? this.eppDomainInfo()!.statuses.join(", ") : ''
    );

    isServerStatus(status: string): boolean {
        // serverHold, serverRenewProhibited, serverTransferProhibited, serverUpdateProhibited, serverDeleteProhibited
        return status.startsWith("server");
    }

    hasServerStatus = computed(() =>
        !!this.eppDomainInfo() && this.eppDomainInfo()!.statuses.some((s) => this.isServerStatus(s))
    );

    hasPendingDeleteStatus = computed(() =>
        !!this.eppDomainInfo() && this.eppDomainInfo()!.statuses.includes('pendingDelete')
    );

    // A domain is within the delete grade period if it has been renewed, and it hasn't been more than 45 days since the
    // previous expiration timestamp (which in turn is the current expiration timestamp minus one year).
    private isDomainWithinDeleteGracePeriod(info: DomainInfo): boolean {
        const dt = DateTime.fromMillis(info.expires);
        return dt.minus({years: 1}).plus({days: 45}) > DateTime.now();
    }

    inDeleteGracePeriod = computed(() =>
        !!this.domainInfo() && this.isDomainWithinDeleteGracePeriod(this.domainInfo()!)
    );

    copyAuthCode() {
        navigator.clipboard.writeText(this.eppDomainInfo()?.auth_code || '').then(() => {
            this.alertService.info("Auth code copied to clipboard");
        });
    }

    syncDates() {
        this.alertService.confirm("Are you sure you want to update the domain dates in our database to match the EPP values?")
            .subscribe((doIt) => {
                if (doIt) {
                    this.busyService.showBusy();
                    this.domainService.syncDomainDates(this.domainInfo()!.name).pipe(
                        finalize(() => {this.busyService.showNotBusy()})
                    ).subscribe({
                        next: (info: OkInfo) => {
                            if (info.ok)
                                this.update();
                            else
                                this.alertService.warn("Nothing to sync!");
                        },
                        error: (err: HttpErrorResponse) => {
                            this.alertService.error("Error syncing domain dates: " + err.error || err.message);
                        }
                    });
                }
            });
    }

    syncStatus() {
        this.alertService.confirm("Are you sure you want to update the domain status in our database to match the EPP value?")
            .subscribe((doIt) => {
                if (doIt) {
                    this.busyService.showBusy();
                    this.domainService.syncDomainStatus(this.domainInfo()!.name).pipe(
                        finalize(() => {this.busyService.showNotBusy()})
                    ).subscribe({
                        next: (info: OkInfo) => {
                            if (info.ok)
                                this.update();
                            else
                                this.alertService.warn("Domain not found to update!");
                        },
                        error: (err: HttpErrorResponse) => {
                            this.alertService.error("Error syncing domain status: " + err.error || err.message);
                        }
                    });
                }
            });
    }

    lock() {
        this.alertService.confirm("Are you sure you want to Lock this domain?").subscribe((doIt) => {
            if (doIt) {
                this.busyService.showBusy();
                this.domainService.lock(this.domainInfo()!.name).pipe(
                    finalize(() => {this.busyService.showNotBusy()})
                ).subscribe({
                    next: (out) => {
                        this.update();
                    },
                    error: (err: HttpErrorResponse) => {
                        this.alertService.error("Error locking domain: " + err.error || err.message);
                    }
                });
            }
        });
    }

    unlock() {
        this.alertService.confirm("Are you sure you want to Unlock this domain?").subscribe((doIt) => {
            if (doIt) {
                this.busyService.showBusy();
                this.domainService.unlock(this.domainInfo()!.name).pipe(
                    finalize(() => {
                        this.busyService.showNotBusy()
                    })
                ).subscribe({
                    next: (out) => {
                        this.update();
                    },
                    error: (err: HttpErrorResponse) => {
                        this.alertService.error("Error unlocking domain: " + err.error || err.message);
                    }
                });
            }
        });
    }

    isLocked = computed(() =>
        this.domainInfo()!.status.includes('pendingDelete') ||
        (
            this.domainInfo()!.status.includes('clientDeleteProhibited') &&
            this.domainInfo()!.status.includes('clientUpdateProhibited') &&
            this.domainInfo()!.status.includes('clientTransferProhibited')
        )
    );

    canLock = computed(() =>
        !this.isLocked() && this.domainInfo()!.in_registry && !this.hasServerStatus() && this.isAdmin
    );

    canUnLock = computed(() =>
        this.isLocked() && this.domainInfo()!.in_registry && !this.hasServerStatus() && this.isAdmin
    );

    canTransfer = computed(() =>
        // A domain can only be transferred when it has been unlocked and while it is still marked as part of our registry
        !this.isLocked() && this.domainInfo()!.in_registry && !this.hasServerStatus() && this.isAdmin
    );

    couldBeDeleted = computed(() =>
        this.domainInfo()!.in_registry && !this.hasPendingDeleteStatus() &&
        !this.domainInfo()!.flags['inquiry'] && !this.domainInfo()!.flags['pick'] && this.isAdmin
    );

    canAllowDelete = computed(() =>
        this.couldBeDeleted() && /^\d{6}$/.test(this.allowDeleteCode())
    );

    canDelete = computed(() =>
        this.couldBeDeleted() && this.allowDelete()
    );

    approveTransfer() {
        this.alertService.confirm("Are you sure you want to Approve Transfer for this domain?").subscribe((doIt) => {
            if (doIt) {
                this.busyService.showBusy();
                this.domainService.approveTransfer(this.domainInfo()!.name).pipe(
                    finalize(() => {
                        this.busyService.showNotBusy()
                    })
                ).subscribe({
                    next: (out) => {
                        this.update();
                    },
                    error: (err: HttpErrorResponse) => {
                        this.alertService.error("Error approving transfer: " + err.error || err.message);
                    }
                });
            }
        });
    }

    /** Validates the entered 2FA code and, if  valid, enables the Delete button
     */
    authorizeDelete() {
        this.busyService.showBusy()
        this.authService.validateMfaCode(this.allowDeleteCode()).pipe(
            finalize(() => {
                this.busyService.showNotBusy()
            })
        ).subscribe( {
            next: (valid) => {
                if (!valid)
                    this.alertService.error("The supplied 2FA code is not valid");
                else {
                    this.allowDeleteCode.set('')
                    this.allowDelete.set(true);
                }
            },
            error: (err: HttpErrorResponse) => {
                this.alertService.error("Error validating 2FA code: " + err.error?.message || err.message);
            }
        });
    }

    delete() {
        this.alertService.confirm("<p>Are you really sure you want to Delete this domain?</p><p>Note this action cannot be undone!</p>")
        .subscribe((doIt) => {
            if (doIt) {
                this.busyService.showBusy();
                this.domainService.delete(this.domainInfo()!.name).pipe(
                    finalize(() => {this.busyService.showNotBusy()})
                ).subscribe({
                    next: () => {
                        this.update();
                    },
                    error: (err: HttpErrorResponse) => {
                        this.alertService.error("Error deleting domain: " + err.error || err.message);
                    }
                });
            }
        });
    }

}
